import type { Timeframe } from '@shared/types/candle'
import type { WsChannel, WsClientMessage, WsServerMessage } from '@shared/types/websocket'

type MessageHandler = (msg: WsServerMessage) => void
type StateHandler = (connected: boolean) => void

const WS_URL = import.meta.env.VITE_WS_URL ?? 'ws://localhost:8000'
const PING_INTERVAL_MS = 25_000 // under Fly.io's 60s idle timeout
const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

class WebSocketService {
  private socket: WebSocket | null = null
  private handlers = new Set<MessageHandler>()
  private stateHandlers = new Set<StateHandler>()
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private backoff = INITIAL_BACKOFF_MS
  private subscriptions: Map<string, { channels: WsChannel[]; timeframes: Timeframe[] }> = new Map()
  private _connected = false

  connect() {
    if (this.socket?.readyState === WebSocket.OPEN) return
    if (this.socket?.readyState === WebSocket.CONNECTING) return
    this.socket = new WebSocket(`${WS_URL}/ws`)
    this.socket.onopen = this.handleOpen
    this.socket.onmessage = this.handleMessage
    this.socket.onclose = this.handleClose
    this.socket.onerror = this.handleError
  }

  disconnect() {
    this.clearTimers()
    this.socket?.close()
    this.socket = null
    this.setConnected(false)
  }

  subscribe(symbols: string[], channels: WsChannel[], timeframes?: Timeframe[]) {
    symbols.forEach((s) => {
      this.subscriptions.set(s, { channels, timeframes: timeframes ?? [] })
    })
    this.send({ type: 'subscribe', symbols, channels, timeframes })
  }

  unsubscribe(symbols: string[]) {
    symbols.forEach((s) => this.subscriptions.delete(s))
    this.send({ type: 'unsubscribe', symbols })
  }

  /** Register a message handler. Returns a cleanup function. */
  on(handler: MessageHandler) {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  /** Register a connection-state listener. Returns a cleanup function. */
  onStateChange(handler: StateHandler) {
    this.stateHandlers.add(handler)
    // Immediately fire with current state
    handler(this._connected)
    return () => this.stateHandlers.delete(handler)
  }

  isConnected() {
    return this._connected
  }

  private setConnected(value: boolean) {
    if (this._connected === value) return
    this._connected = value
    this.stateHandlers.forEach((h) => h(value))
  }

  private send(msg: WsClientMessage) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(msg))
    }
  }

  private handleOpen = () => {
    this.setConnected(true)
    this.backoff = INITIAL_BACKOFF_MS
    this.startPing()
    // Re-subscribe after reconnect
    this.subscriptions.forEach(({ channels, timeframes }, symbol) => {
      this.send({ type: 'subscribe', symbols: [symbol], channels, timeframes })
    })
  }

  private handleMessage = (event: MessageEvent) => {
    try {
      const msg = JSON.parse(event.data as string) as WsServerMessage
      this.handlers.forEach((h) => h(msg))
    } catch {
      // ignore malformed frames
    }
  }

  private handleClose = () => {
    this.setConnected(false)
    this.clearTimers()
    this.scheduleReconnect()
  }

  private handleError = () => {
    this.socket?.close()
  }

  private scheduleReconnect() {
    this.reconnectTimer = setTimeout(() => {
      this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS)
      this.connect()
    }, this.backoff)
  }

  private startPing() {
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping' })
    }, PING_INTERVAL_MS)
  }

  private clearTimers() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.pingTimer) clearInterval(this.pingTimer)
    this.reconnectTimer = null
    this.pingTimer = null
  }
}

// Singleton
export const wsService = new WebSocketService()
